/**
 * useEnvironmentAttestation — React Query hook for fetching an
 * EnvironmentAttestation proof artifact for a CERTIFIED run.
 *
 * Only enabled when a run has an attestation_id (i.e. is CERTIFIED).
 * In demo mode, returns static mock data matching the demo CERTIFIED run.
 */

import { useQuery } from '@tanstack/react-query';
import { isDemoModeEnabled } from '../demo/demoMode';
import { fetchEnvironmentAttestation } from '../api/environmentRuns';
import type { EnvironmentAttestation } from '../types/environmentRun';

// ── Demo data ────────────────────────────────────────────────────────────

const DEMO_ATTESTATION: EnvironmentAttestation = {
  attestation_id: 'att_env_001',
  run_id: 'env_run_001',
  construct_id: 'tremor-seismic-v3',
  construct_version: '3.1.0',
  commitment_hash: 'e5f6a7b8c9d0e1f2a3b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f7081920',
  ground_truth_hash: 'f6a7b8c9d0e1f2a3b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f708192a3b',
  evidence_hash: 'd4e5f6a7b8c9d0e1f2a3b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f70819',
  receipt_hash: 'a3f2b1c4d5e6f708192a3b4c5d6e7f80a1b2c3d4e5f60718293a4b5c6d7e8f90',
  checks_executed: 4,
  checks_passed: 3,
  checks_failed: 1,
  has_critical_failures: false,
  composite_score: 0.75,
  brier_score: 0.142,
  replay_count: 2,
  replay_verified: true,
  scorer_version: 'theatre_check_runner:1.0',
  attestation_version: '1.0',
  issued_at: '2026-03-21T14:28:34Z',
  expires_at: '2026-06-19T14:28:34Z',
  revoked_at: null,
  revocation_reason: null,
  revoked_by: null,
  is_expired: false,
  is_current: true,
};

export function useEnvironmentAttestation(runId: string | null) {
  const isDemo = isDemoModeEnabled();

  return useQuery({
    queryKey: ['environmentAttestation', runId],
    queryFn: async () => {
      if (isDemo) return DEMO_ATTESTATION;
      return fetchEnvironmentAttestation(runId!);
    },
    enabled: !!runId,
    staleTime: 60_000,
  });
}
